import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { SiteSettings } from '@/models/siteSettings'
import { SiteSettingsService } from '@/services/site-settings.service'
import { setDefaultScreeningColorProfile } from '@/theme/colorProfiles'

export const useSiteSettingsStore = defineStore('site-settings', () => {
  const settings = ref<SiteSettings | null>(null)
  const isLoading = ref(false)
  const error = ref<unknown>(null)
  const fetchedAt = ref(0)
  let inFlight: Promise<void> | null = null

  const hasLoaded = computed(() => fetchedAt.value > 0)

  async function ensure(opts?: { force?: boolean }) {
    if (!opts?.force && hasLoaded.value) return
    if (inFlight) return inFlight

    isLoading.value = true
    error.value = null

    inFlight = (async () => {
      try {
        settings.value = await SiteSettingsService.get()
        setDefaultScreeningColorProfile(settings.value.colorProfile)
        fetchedAt.value = Date.now()
      } catch (e) {
        error.value = e
      } finally {
        isLoading.value = false
        inFlight = null
      }
    })()

    return inFlight
  }

  function invalidate() {
    fetchedAt.value = 0
  }

  return {
    settings,
    isLoading,
    error,
    hasLoaded,
    ensure,
    invalidate,
  }
})
